<script setup lang="ts">
import { computed } from 'vue';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { AppSvgIcon, AppFadeInOutTransition } from '@libs/ui';
import type { CssCustomPropertyFont } from '@libs/ui';

import { MonthsSelectorInnerView } from '../../priceRibbon';
import type { PriceRibbonMonth } from '../../priceRibbon';

interface PriceCalendarHeaderProps {
  shouldShowMonthsSelector: boolean;
  months: PriceRibbonMonth[];
  selectedMonthIsoDate: string;
  headerFont: CssCustomPropertyFont;
}

const props = defineProps<PriceCalendarHeaderProps>();

interface PriceCalendarHeaderEmits {
  (eventName: 'toggle-months-selector'): void;
  (eventName: 'select-month', month: Date): void;
}

const emit = defineEmits<PriceCalendarHeaderEmits>();

const chevronIcon = computed<'chevronDown' | 'chevronUp'>(() =>
  props.shouldShowMonthsSelector ? 'chevronUp' : 'chevronDown'
);

const currentMonth = computed<string>(() => {
  const date = new SemboDate(new Date(props.selectedMonthIsoDate));
  return date.format(DateFormat.FullMonthAndYear);
});
</script>

<template>
  <div class="price-calendar-header">
    <div class="actions">
      <button
        class="month-button"
        :class="{ [props.headerFont]: true }"
        @click="emit('toggle-months-selector')"
      >
        {{ currentMonth }}
        <AppSvgIcon :name="chevronIcon" size="16" color="color-soft-black" />
      </button>
      <slot name="headerSecondaryAction" />
    </div>

    <div class="months">
      <AppFadeInOutTransition>
        <MonthsSelectorInnerView
          v-if="shouldShowMonthsSelector"
          :months="props.months"
          :selected-month-iso-date="props.selectedMonthIsoDate"
          @confirm="emit('select-month', $event)"
        />
      </AppFadeInOutTransition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-calendar-header {
  width: 100%;
  height: 100%;
  position: relative;

  .actions {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .month-button {
      all: unset;
      cursor: pointer;
      color: var(--color-soft-black);

      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      &.font-label-xl-light {
        @include font-label-xl-light;
      }

      &.font-label-l-bold {
        @include font-label-l-bold;
      }
    }
  }

  .months {
    background: var(--color-white);
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
  }
}
</style>
